import {
  Box,
  Button,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  IconButton,
} from '@mui/material';
import { useState, useEffect, useRef, useMemo } from 'react';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useApp } from '@context/AppContext';
import { PAGINATION_LOGIC } from '@constants/customPaginationConstants';

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: 2,
  display: 'flex',
  gap: '2px',
  justifyContent: 'center',
  width: '100%',
});

interface CustomPaginationProps {
  count: number;
  initialRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  onStateChange?: (state: { page: number; rowsPerPage: number }) => void;
  disabled?: boolean;
  showRowsPerPage?: boolean;
  currentPage?: number;
}

const CustomPagination = ({
  count,
  initialRowsPerPage = 25,
  rowsPerPageOptions = [10, 25, 50, 100],
  onStateChange,
  disabled = false,
  showRowsPerPage = true,
  currentPage,
}: CustomPaginationProps) => {
  const theme = useTheme();
  const { isMobileScreen } = useApp();
  const isInitialMount = useRef(true);
  const selectRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Use controlled page state if provided, otherwise use internal state
  const [internalPage, setInternalPage] = useState<number>(
    PAGINATION_LOGIC.DEFAULT_PAGE
  );
  const page = currentPage !== undefined ? currentPage : internalPage;

  // Use useMemo to derive the display value from props to avoid sync issues
  const displayValue = useMemo(() => {
    const smallestOption = Math.min(...rowsPerPageOptions);
    if (count > 0 && count < smallestOption) {
      return smallestOption;
    }

    if (rowsPerPageOptions.includes(initialRowsPerPage)) {
      return initialRowsPerPage;
    }
    return smallestOption;
  }, [initialRowsPerPage, count, rowsPerPageOptions]);

  const totalPages = Math.max(1, Math.ceil(count / displayValue));

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (currentPage === undefined) {
        setInternalPage(1);
      }
      if (onStateChange) {
        onStateChange({
          page: currentPage || PAGINATION_LOGIC.DEFAULT_PAGE,
          rowsPerPage: displayValue,
        });
      }
    }
  }, [currentPage, initialRowsPerPage, onStateChange, displayValue]);

  const handlePageChange = (_event: unknown, newPage: number) => {
    const validPage = Math.max(1, Math.min(newPage, totalPages));
    if (validPage !== page) {
      if (currentPage === undefined) {
        setInternalPage(validPage);
      }
      if (onStateChange) {
        onStateChange({ page: validPage, rowsPerPage: displayValue });
      }
    }
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newRowsPerPage = Number(event.target.value);
    const newTotalPages = Math.max(1, Math.ceil(count / newRowsPerPage));
    const validPage = Math.min(page, newTotalPages);

    if (onStateChange) {
      onStateChange({ page: validPage, rowsPerPage: newRowsPerPage });
    }
  };

  const handleSelectOpen = (event: React.SyntheticEvent<Element, Event>) => {
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleSelectClose = () => {
    setAnchorEl(null);
  };

  const renderPaginationItems = () => {
    const items = [];
    const range = isMobileScreen
      ? PAGINATION_LOGIC.MOBILE_RANGE
      : PAGINATION_LOGIC.DESKTOP_RANGE;
    const start = Math.max(1, page - range);
    const end = Math.min(totalPages, page + range);

    items.push(
      <li key="previous">
        <IconButton
          onClick={() => handlePageChange(null, page - 1)}
          disabled={page === 1}
          aria-label="Go to previous page"
          sx={{
            padding: '4px',
            minWidth: '35px',
            width: '35px',
            borderRadius: 10,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            flexShrink: 0,
          }}
        >
          <ChevronLeft />
        </IconButton>
      </li>
    );

    items.push(
      <li key={1}>
        <Button
          onClick={() => handlePageChange(null, 1)}
          variant="text"
          disableElevation
          sx={{
            '&.MuiButton-root': {
              lineHeight: 1.75,
            },
            minWidth: '35px',
            width: '35px',
            padding: '4px 4px 2px 4px',
            borderRadius: 10,
            backgroundColor:
              page === 1 ? theme.palette.teal[200] : 'transparent',
            border: 'none',
            color: theme.palette.text.primary,
            textTransform: 'none',
            flexShrink: 0,
          }}
        >
          {1}
        </Button>
      </li>
    );

    if (
      start >
      (isMobileScreen
        ? PAGINATION_LOGIC.ELLIPSIS_THRESHOLD_MOBILE
        : PAGINATION_LOGIC.ELLIPSIS_THRESHOLD_DESKTOP)
    ) {
      items.push(
        <li key="start-ellipsis">
          <Button
            disabled
            sx={{
              padding: '4px 4px 2px 4px',
              minWidth: '35px',
              width: '35px',
              borderRadius: 10,
              backgroundColor: 'transparent',
              border: 'none',
              color: theme.palette.text.disabled,
              flexShrink: 0,
            }}
            variant="text"
          >
            ...
          </Button>
        </li>
      );
    }

    for (let i = start; i <= end; i++) {
      if (i !== 1 && i !== totalPages) {
        items.push(
          <li key={i}>
            <Button
              onClick={() => handlePageChange(null, i)}
              variant="text"
              disableElevation
              sx={{
                '&.MuiButton-root': {
                  lineHeight: 1.75,
                },
                minWidth: '35px',
                width: '35px',
                padding: '4px 4px 2px 4px',
                borderRadius: 10,
                backgroundColor:
                  i === page ? theme.palette.teal[200] : 'transparent',
                border: 'none',
                color: theme.palette.text.primary,
                textTransform: 'none',
                flexShrink: 0,
              }}
            >
              {i}
            </Button>
          </li>
        );
      }
    }

    if (end < totalPages - 1) {
      items.push(
        <li key="end-ellipsis">
          <Button
            disabled
            sx={{
              padding: '4px 4px 2px 4px',
              minWidth: '35px',
              width: '35px',
              borderRadius: 10,
              backgroundColor: 'transparent',
              border: 'none',
              color: theme.palette.text.disabled,
              flexShrink: 0,
            }}
            variant="text"
          >
            ...
          </Button>
        </li>
      );
    }

    if (totalPages > 1) {
      items.push(
        <li key={totalPages}>
          <Button
            onClick={() => handlePageChange(null, totalPages)}
            variant="text"
            disableElevation
            sx={{
              '&.MuiButton-root': {
                lineHeight: 1.75,
              },
              minWidth: '35px',
              width: '35px',
              padding: '4px 4px 2px 4px',
              borderRadius: 10,
              backgroundColor:
                page === totalPages ? theme.palette.teal[200] : 'transparent',
              border: 'none',
              color: theme.palette.text.primary,
              textTransform: 'none',
              flexShrink: 0,
            }}
          >
            {totalPages}
          </Button>
        </li>
      );
    }

    items.push(
      <li key="next">
        <IconButton
          onClick={() => handlePageChange(null, page + 1)}
          disabled={page === totalPages}
          aria-label="Go to next page"
          sx={{
            padding: '4px',
            minWidth: '35px',
            width: '35px',
            borderRadius: 10,
            backgroundColor: theme.palette.background.paper,
            border: 'none',
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            flexShrink: 0,
          }}
        >
          <ChevronRight />
        </IconButton>
      </li>
    );

    return items;
  };

  const rowsPerPageSelectSx = useMemo(
    () => ({
      width: '65px',
      height: '30px',
      fontSize: 'inherit',
      color: theme.palette.text.primary,
      '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.background.paper,
      },
      '& .MuiSelect-outlined': {
        '&.Mui-focused fieldset': {
          border: `solid 1px ${alpha(theme.palette.primary.main, 0.5)}`,
        },
      },
      '& .MuiSelect-select': {
        padding: '0 0 0 5px !important',
      },
      '& .MuiSelect-icon': {
        right: '4px',
        color: theme.palette.text.secondary,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1px !important',
        borderColor: theme.palette.divider,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.text.secondary,
      },
      '& .MuiPaper-root': {
        marginTop: '4px',
      },
    }),
    [theme]
  );

  const menuProps = {
    anchorEl: anchorEl,
    anchorOrigin: {
      vertical: 'bottom' as const,
      horizontal: 'left' as const,
    },
    transformOrigin: {
      vertical: 'top' as const,
      horizontal: 'left' as const,
    },
    slotProps: {
      paper: {
        sx: {
          zIndex: 1000,
          maxHeight: 200,
          overflow: 'auto',
          position: 'fixed' as const,
          margin: 0,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      },
    },
    disableScrollLock: true,
    BackdropProps: {
      invisible: true,
    },
  };

  if (isMobileScreen) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          p: 2,
          backgroundColor: theme.palette.grey[300],
          borderRadius: 5,
        }}
      >
        {showRowsPerPage && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">Showing</Typography>
            <FormControl size="small" sx={{ minWidth: 65 }}>
              <Select
                ref={selectRef}
                value={displayValue}
                onChange={handleRowsPerPageChange}
                onOpen={handleSelectOpen}
                onClose={handleSelectClose}
                disabled={disabled}
                inputProps={{ 'aria-label': 'Select number of rows per page' }}
                MenuProps={menuProps}
                sx={rowsPerPageSelectSx}
              >
                {rowsPerPageOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2">of {count}</Typography>
          </Stack>
        )}
        <Box
          sx={{
            backgroundColor: theme.palette.grey[300],
            p: 0.5,
            borderRadius: 5,
            minWidth: '10%',
            maxWidth: '30%',
          }}
        >
          <nav>
            <List>{renderPaginationItems()}</List>
          </nav>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 1,
      }}
    >
      {showRowsPerPage && (
        <Stack direction="row" alignItems="center" width="40%">
          <Typography sx={{ mr: 1 }}>Showing</Typography>
          <FormControl size="small" sx={{ minWidth: 65 }}>
            <Select
              ref={selectRef}
              value={displayValue}
              onChange={handleRowsPerPageChange}
              onOpen={handleSelectOpen}
              onClose={handleSelectClose}
              disabled={disabled}
              inputProps={{ 'aria-label': 'Select number of rows per page' }}
              MenuProps={menuProps}
              sx={rowsPerPageSelectSx}
            >
              {rowsPerPageOptions.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={{ ml: 1 }}>of {count}</Typography>
        </Stack>
      )}
      <Box
        sx={{
          backgroundColor: theme.palette.grey[300],
          p: 0.5,
          borderRadius: 5,
          minWidth: '10%',
          maxWidth: '60%',
        }}
      >
        <nav>
          <List>{renderPaginationItems()}</List>
        </nav>
      </Box>
    </Box>
  );
};

export default CustomPagination;
